import { ArrowLeftIcon, ArrowRightIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const getVisiblePages = (): (number | '...')[] => {
    const maxVisible = 5;
    const pages: (number | '...')[] = [];

    if (totalPages <= maxVisible) {
      // Show all pages — no padding; width grows with actual page count
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage <= 3) {
        // Near the beginning: [1, 2, 3, 4, ...]
        pages.push(2, 3, 4, '...');
      } else if (currentPage >= totalPages - 2) {
        // Near the end: [..., total-3, total-2, total-1, total]
        if (currentPage === totalPages - 1) {
          // On penultimate page: [..., total-2, total-1, total]
          pages.push('...', totalPages - 2, totalPages - 1, totalPages);
        } else if (currentPage === totalPages) {
          // On last page: [..., total-2, total-1, total]
          pages.push('...', totalPages - 2, totalPages - 1, totalPages);
        } else {
          // On totalPages - 2: [..., total-3, total-2, total-1, total]
          pages.push('...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
        }
      } else {
        // In the middle: [..., current-1, current, current+1, ...]
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...');
      }
    }
    
    return pages;
  };

  return (
    <div className="flex items-center justify-center py-6">
      <div className="flex w-full max-w-96 items-center justify-center gap-1 sm:justify-between sm:gap-0">
        <LureoButton
          variant="outline-2"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-8 w-8 shrink-0 px-0 sm:mr-1 sm:h-9 sm:w-auto sm:px-3 rounded-lureo-radius-button-pill"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Previous</span>
        </LureoButton>

        <div className="flex items-center gap-1">
          {getVisiblePages().map((page, index) => (
            <div key={index} className="flex h-8 items-center justify-center sm:h-9">
              {page === '...' ? (
                <span className="px-1 py-1 text-lureo-ink-subtlest sm:px-2">...</span>
              ) : (
                <LureoButton
                  variant={currentPage === page ? 'solid' : 'outline-2'}
                  aria-current={currentPage === page ? 'page' : undefined}
                  onClick={() => onPageChange(page)}
                  className="h-8 w-8 px-0 rounded-full tabular-nums sm:h-9 sm:w-9"
                >
                  {page}
                </LureoButton>
              )}
            </div>
          ))}
        </div>

        <LureoButton
          variant="outline-2"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-8 w-8 shrink-0 px-0 sm:ml-1 sm:h-9 sm:w-auto sm:px-3 rounded-lureo-radius-button-pill"
        >
          <span className="hidden sm:inline">Next</span>
          <ArrowRightIcon className="h-4 w-4" />
        </LureoButton>
      </div>
    </div>
  );
}
